import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import useIntents from '@/hooks/useIntents';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { Intent } from '@/shared/types/intent';
import { EventName } from '@/shared/types/segment';

import { ErrorDescription } from './ErrorDescription';
import { ErrorRequest } from './ErrorRequest';
import { errorHandler } from './util';

import styles from './ModalAutoBrain.module.scss';

interface Props {
  error: { extra: { internal_message?: string } };
}

const getConflictingIntent = (
  intents: Intent[],
  errors: { [key: string]: string },
  isConflict: boolean
) => {
  if (!isConflict) {
    return null;
  }
  let intent;
  for (const value of Object.values(errors)) {
    intent = intents?.find((intent) => intent.intent == value);
    if (intent) {
      break;
    }
  }
  return intent;
};

const getAutoDialogErrorType = (intentConflict: boolean, notGenerated: boolean) => {
  if (intentConflict) {
    return 'intent_conflict';
  }
  if (notGenerated) {
    return 'not_generated';
  }
  return 'other';
};

const AutoBrainError = ({ error }: Props) => {
  const navigate = useNavigate();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { intents } = useIntents(brainId);

  const { intentConflict, notGenerated, title, subtitle } = errorHandler({
    message: error?.extra?.internal_message,
  });
  const intent = getConflictingIntent(intents, error.extra, intentConflict);

  const navigateToIntent = () => {
    dispatch(popModal());
    navigate(`/${slug}/ai-agents/${brainId}/workflows/intents/${intent.intent}`);
  };

  const errorType = getAutoDialogErrorType(intentConflict, notGenerated);
  useEffect(() => {
    trackEvent(EventName.AutoDialogError, { type: errorType });
  }, [errorType]);

  return (
    <div className={styles.errorContainer}>
      {notGenerated ? <ErrorDescription /> : <ErrorRequest />}
      <span className="text-xl leading-7 font-bold">{title}</span>
      <span className="text-sm leading-5 font-normal">{subtitle}</span>

      {intent && (
        <div className={styles.intent}>
          <span className="text-sm leading-5 font-normal">{t('common.intent')}:</span>{' '}
          <Button variant="link" size="sm" onClick={navigateToIntent}>
            {intent.intent}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AutoBrainError;
